import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { TrendingUp, TrendingDown, DollarSign, Activity, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt4 = (v) => `$${(v ?? 0).toFixed(4)}`;
const fmt  = (v) => {
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toFixed(2)}`;
};
const pct = (v, d=3) => `${(v ?? 0) >= 0 ? '+' : ''}${((v ?? 0)).toFixed(d)}%`;

const PORTFOLIO_COLORS = {
  bedrock:  { badge: 'bg-emerald-100 text-emerald-700', accent: '#10b981' },
  fortress: { badge: 'bg-sky-100 text-sky-700',         accent: '#0ea5e9' },
  horizon:  { badge: 'bg-indigo-100 text-indigo-700',   accent: '#6366f1' },
  velocity: { badge: 'bg-amber-100 text-amber-700',     accent: '#f59e0b' },
  summit:   { badge: 'bg-orange-100 text-orange-700',   accent: '#f97316' },
  nebula:   { badge: 'bg-purple-100 text-purple-700',   accent: '#a855f7' },
};

export default function NAVPage() {
  const [navs, setNavs] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [fundNavs, setFundNavs] = useState([]);
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPortfolio, setSelectedPortfolio] = useState('horizon');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [navRes, pricingRes, fundsRes] = await Promise.all([
          authFetch(`${API_BASE}/api/lms/nav`),
          authFetch(`${API_BASE}/api/lms/nav/pricing`),
          authFetch(`${API_BASE}/api/lms/nav/funds`),
        ]);
        if (navRes.ok) setNavs(await navRes.json());
        if (pricingRes.ok) setPricing(await pricingRes.json());
        if (fundsRes.ok) setFundNavs(await fundsRes.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/lms/nav/portfolio/${selectedPortfolio}/history?days=30`);
        if (res.ok) {
          const data = await res.json();
          setHistory(prev => ({ ...prev, [selectedPortfolio]: data }));
        }
      } catch {}
    };
    if (selectedPortfolio) loadHistory();
  }, [selectedPortfolio]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
    </div>
  );

  const totalAUM = navs.reduce((s, n) => s + (n.total_aum ?? 0), 0);
  const avgReturn = navs.length
    ? navs.reduce((s, n) => s + (n.daily_return_pct ?? 0), 0) / navs.length
    : 0;

  const pricingMap = pricing.reduce((m, p) => ({ ...m, [p.portfolio_type]: p }), {});
  const histData = history[selectedPortfolio] || [];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">NAV & Pricing Engine</h1>
        <p className="text-sm text-gray-500 mt-1">
          Daily Net Asset Values, buy/sell pricing, and wholesale fund NAVs
        </p>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total AUM</p>
            <p className="text-2xl font-bold">{fmt(totalAUM)}</p>
            <p className="text-xs text-gray-400 mt-1">All portfolios</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Daily Return</p>
            <p className={`text-2xl font-bold ${avgReturn >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {pct(avgReturn)}
            </p>
            <p className="text-xs text-gray-400 mt-1">Today vs yesterday</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Pricing Cycles</p>
            <p className="text-2xl font-bold text-gray-900">Daily</p>
            <p className="text-xs text-gray-400 mt-1">NAV calculated each day</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Illiquid Fund NAVs</p>
            <p className="text-2xl font-bold text-gray-900">Protected</p>
            <p className="text-xs text-emerald-600 mt-1">Property: quarterly update</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="current">
        <TabsList>
          <TabsTrigger value="current">Current NAV & Pricing</TabsTrigger>
          <TabsTrigger value="history">NAV History</TabsTrigger>
          <TabsTrigger value="funds">Wholesale Fund NAVs</TabsTrigger>
        </TabsList>

        {/* Current NAV */}
        <TabsContent value="current">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {navs.map(n => {
              const p = pricingMap[n.portfolio_type] || {};
              const col = PORTFOLIO_COLORS[n.portfolio_type] || { badge: 'bg-gray-100 text-gray-700', accent: '#6b7280' };
              return (
                <Card key={n.portfolio_type} className="overflow-hidden">
                  <div className="h-1" style={{ backgroundColor: col.accent }} />
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-semibold ${col.badge}`}>
                        {n.portfolio_type}
                      </span>
                      <span className={`text-sm font-medium ${(n.daily_return_pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {pct(n.daily_return_pct)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* NAV */}
                    <div className="text-center py-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">NAV per Unit</p>
                      <p className="text-3xl font-bold text-gray-900">{fmt4(n.nav_per_unit)}</p>
                      <p className="text-xs text-gray-400">vs prev {fmt4(n.previous_nav)}</p>
                    </div>
                    {/* Pricing */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-emerald-50 rounded p-2 text-center">
                        <p className="text-xs text-emerald-600 font-medium">BUY PRICE</p>
                        <p className="text-base font-bold text-emerald-700">{fmt4(p.application_price)}</p>
                        <p className="text-xs text-emerald-500">Entry spread {((p.entry_spread_pct ?? 0) * 100).toFixed(2)}%</p>
                      </div>
                      <div className="bg-orange-50 rounded p-2 text-center">
                        <p className="text-xs text-orange-600 font-medium">SELL PRICE</p>
                        <p className="text-base font-bold text-orange-700">{fmt4(p.redemption_price)}</p>
                        <p className="text-xs text-orange-500">Exit spread {((p.exit_spread_pct ?? 0) * 100).toFixed(2)}%</p>
                      </div>
                    </div>
                    {/* AUM */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>AUM</span>
                      <span className="font-semibold">{fmt(n.total_aum)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Units Outstanding</span>
                      <span className="font-mono">{(n.units_outstanding ?? 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Active Investors</span>
                      <span>{n.active_investors}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* NAV History */}
        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">30-Day NAV History</CardTitle>
                <div className="flex gap-2">
                  {['bedrock','fortress','horizon','velocity','summit','nebula'].map(pt => (
                    <button
                      key={pt}
                      onClick={() => setSelectedPortfolio(pt)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        selectedPortfolio === pt
                          ? (PORTFOLIO_COLORS[pt]?.badge || 'bg-gray-200 text-gray-700')
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {pt}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Simple sparkline table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">NAV per Unit</TableHead>
                    <TableHead className="text-right">Daily Return</TableHead>
                    <TableHead className="text-right">AUM</TableHead>
                    <TableHead className="text-right">Units Out.</TableHead>
                    <TableHead className="text-right">Liquid</TableHead>
                    <TableHead className="text-right">Illiquid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...histData].reverse().map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(r.date).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {fmt4(r.nav_per_unit)}
                      </TableCell>
                      <TableCell className={`text-right text-sm font-medium ${(r.daily_return_pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {pct(r.daily_return_pct)}
                      </TableCell>
                      <TableCell className="text-right">{fmt(r.total_aum)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {(r.units_outstanding ?? 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right text-emerald-600">{fmt(r.liquid_value)}</TableCell>
                      <TableCell className="text-right text-orange-500">{fmt(r.illiquid_value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fund NAVs */}
        <TabsContent value="funds">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fundNavs.map(f => {
              const isIlliquid = f.fund_type === 'fund1_property';
              const isSemiLiquid = f.fund_type === 'fund2_credit';
              return (
                <Card key={f.fund_type} className="overflow-hidden">
                  <div className={`h-1 ${isIlliquid ? 'bg-red-400' : isSemiLiquid ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{f.fund_name}</CardTitle>
                      <Badge className={isIlliquid ? 'bg-red-100 text-red-700' : isSemiLiquid ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                        {isIlliquid ? 'ILLIQUID' : isSemiLiquid ? 'SEMI-LIQUID' : 'LIQUID'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Current NAV</p>
                        <p className="text-2xl font-bold">{fmt4(f.nav_per_unit)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Daily Change</p>
                        <p className={`text-lg font-semibold ${(f.daily_change ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {pct(f.daily_change_pct, 4)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500">
                        {isIlliquid
                          ? 'Quarterly redemptions — 30-day notice required. Protected by Helix crossing engine.'
                          : isSemiLiquid
                          ? 'Monthly redemptions — 14-day notice required. Rarely needs to be sold.'
                          : 'Daily redemptions — T+2 settlement.'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
